import { createContext, useContext } from 'react';
import { richTextFreeForm as richTextFreeFormConfig } from 'common/components/RichTextEditor/CKEditorConfig';
import {
  isCKEditorHighlightEnabled,
  isInlineCommentingEnabled,
} from '../helpers/featureFlags';

export const getImageUploadConfig = ({
  projectId,
  projectToken,
  richTextFreeForm,
  enableImageUpload,
}: {
  projectId: string;
  projectToken: string;
  richTextFreeForm: typeof richTextFreeFormConfig;
  enableImageUpload: boolean;
}) => ({
  ...richTextFreeForm,
  image: {
    resizeUnit: '%',
    resizeOptions: [
      {
        name: 'resizeImage:original',
        value: null,
        label: 'Default image width',
      },
      {
        name: 'resizeImage:25',
        value: '25',
        label: '25% page width',
      },
      {
        name: 'resizeImage:50',
        value: '50',
        label: '50% page width',
      },
      {
        name: 'resizeImage:75',
        value: '75',
        label: '75% page width',
      },
      {
        name: 'resizeImage:100',
        value: '100',
        label: '100% page width',
      },
    ],
    toolbar: ['resizeImage', 'linkImage'],
    upload: {
      types: ['jpg', 'jpeg', 'png', 'gif', 'bmp'],
    },
  },
  toolbar: {
    ...richTextFreeForm.toolbar,
    items: isInlineCommentingEnabled
      ? [
          'heading',
          '|',
          'fontColor',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'subscript',
          'superscript',
          ...(isCKEditorHighlightEnabled ? ['highlight'] : []),
          'bulletedList',
          'numberedList',
          '|',
          'indent',
          'outdent',
          '|',
          'link',
          '|',
          enableImageUpload ? 'uploadImage' : '',
          '|',
          'insertTable',
          '|',
          'inlineComment',
          '|',
          'addMinitableChart',
        ]
      : [
          'heading',
          '|',
          'fontColor',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'subscript',
          'superscript',
          ...(isCKEditorHighlightEnabled ? ['highlight'] : []),
          'bulletedList',
          'numberedList',
          '|',
          'indent',
          'outdent',
          '|',
          'link',
          '|',
          enableImageUpload ? 'uploadImage' : '',
          '|',
          'insertTable',
          '|',
          'addMinitableChart',
        ],
  },
  simpleUpload: {
    uploadUrl: `${
      process.env.REACT_APP_IMAGE_API || ''
    }/projects/${projectId}/upload`,
    withCredentials: false,
    headers: {
      Authorization: `Bearer ${projectToken}`,
    },
  },
});

export type CKEditorContextType = {
  enableImageUpload: boolean;
};

export const CKEditorContext = createContext<CKEditorContextType>({
  enableImageUpload: false,
});

export const useCKEditorContext = () => {
  const context = useContext(CKEditorContext);
  if (context === undefined) {
    throw new Error('useCKEditorContext must be within TodoProvider');
  }
  return context;
};
